import {Component, OnDestroy, OnInit} from '@angular/core';
import {MapService} from '../../services/map.service';
import * as mapboxgl from 'mapbox-gl';
import {MapIconsService} from '../../services/map-icons.service';
import {ComponentCanDeactivate} from '../../common/can-deactivate/component-can-deactivate';
import {animate, style, transition, trigger} from '@angular/animations';
import {FeatureCollection, Geometry} from 'geojson';
import {GisMachine} from "../../model/gis-machine";
import {MachinesService} from "../../services/machines.service";
import {MapLayerControl} from '../../model/custom/map/map-layer-control';
import {MachineFilters} from "../../model/filters/machine-filters";
import {MachineModel} from "../../model/machine-model";
import {HierarchyService} from "../../services/hierarchy.service";
import {MachineModelsService} from "../../services/machine-models.service";
import {MachineGroupsService} from "../../services/machine-groups.service";
import {MachineGroup} from "../../model/machine-group";
import {TreeNode} from "primeng/api";
import {Geocoding} from "../../model/geocoding";
import {GeocodingService} from "../../services/geocoding.service";
import {CommonMessagesService} from "../../services/common-messages.service";
import {ColorService} from "../../services/color.service";
import {AuthGuard} from "../../common/auth-guard.service";
import {AlarmsService} from 'src/app/services/alarms.service';
import {AlarmDefinitionSummary} from 'src/app/model/alarm-definition-summary';
import {TranslocoService} from "@ngneat/transloco";

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({opacity: 0}),
        animate(500, style({opacity: 1}))
      ]),
      transition(':leave', [
        animate(500, style({opacity: 0}))
      ])
    ])
  ]
})
export class MapComponent implements OnInit, OnDestroy, ComponentCanDeactivate {

  public readonly DEFAULT_ZOOM: number = 5;
  public readonly DEFAULT_LATITUDE: number = 40.41693046935591;
  public readonly DEFAULT_LONGITUDE: number = -3.703872132326427;

  public filtersVisible: boolean = false;
  public currentFilters: MachineFilters;
  public displayDetails: boolean = false;

  public mapIsLoading: boolean;

  public detailsMode: 'machine' | 'cluster';
  public machineDetailsUuid: string;
  public machineDetailsUuids: string[];

  public hierarchy: TreeNode[];
  public organizationCodes: any[];
  public organizations: any[];
  public models: MachineModel[];
  public countries: { name: string }[];
  public communicationTypes: any[];
  public groups: MachineGroup[];
  public alarms: AlarmDefinitionSummary[];

  public isStateDisabled: boolean;
  public isLocalityDisabled: boolean;

  private readonly PERMISSION_MANAGE_ADVANCED_MACHINES: string = 'PERMISSION_MANAGE_ADVANCED_MACHINES';

  private language: string;
  private deleted: boolean = false;

  private map: mapboxgl.Map;
  private mapData: FeatureCollection<Geometry>;
  private representationVisibility: Map<number, boolean>;

  private gisMachines: GisMachine[];
  private icons: any[];

  private states: { name: string }[];
  private localities: { name: string }[];
  private geocodings: Geocoding[];
  private countryStates: Map<string, Set<string>>;
  private stateLocalities: Map<string, Set<string>>;


  constructor(public mapIconsService: MapIconsService,
              private mapService: MapService,
              private geocodingService: GeocodingService,
              private machinesService: MachinesService,
              public authGuard: AuthGuard,
              private hierarchyService: HierarchyService,
              private machineModelsService: MachineModelsService,
              private machineGroupsService: MachineGroupsService,
              public commonMessagesService: CommonMessagesService,
              private colorService: ColorService,
              private readonly i18n: TranslocoService,
              private readonly alarmsService: AlarmsService) {
    this.gisMachines = [];
    this.mapIsLoading = true;
    this.currentFilters = new MachineFilters();
    this.currentFilters.deleted = false;
    this.communicationTypes = [{label: "GPRS", value: 0}, {label: "WIFI", value: 1}];
  }

  ngOnInit(): void {
    this.language = this.i18n.getActiveLang();
    this.loadFilters();
    this.representationVisibility = new Map<number, boolean>();
    this.loadMapAndMachinesData();
  }

  private loadMapAndMachinesData(): void {
    this.map = this.mapService.buildMap(this.DEFAULT_ZOOM, this.DEFAULT_LATITUDE, this.DEFAULT_LONGITUDE, 'globalMap');
    this.map.on('load', () => {
      this.loadMachineData();
    });
  }

  loadMachineData(): void {
    this.icons = [];
    this.trimFieldsForm(this.currentFilters);
    this.machinesService.getGisMachines(this.currentFilters).subscribe(response => {
      this.gisMachines = response.machines;
      this.gisMachines.forEach((gisMachine, index) => {
        this.icons.push({
          id: index + 1,
          data: this.getIconUrl(gisMachine)
        });
        gisMachine.iconId = index + 1;
        this.representationVisibility.set(index + 1, true);
      });
      this.buildMapImagesFromIcons();
      this.fitMapToData();
    });
  }

  getIconUrl(gisMachine: GisMachine): string {
    if (gisMachine.telemetryData) {
      if (gisMachine.telemetryData.hasAlarms) {
        return 'alarms.png';
      }
      if (gisMachine.telemetryData.hasEvents) {
        return 'events.png';
      }
      if (gisMachine.telemetryData.isPoweredOn) {
        return 'connected.png';
      }
    }
    return 'disconnected.png';
  }

  private buildMapImagesFromIcons(): void {
    const loadedRepresentationIconIds: any[] = [];
    const promises = [];
    for (const icon of this.icons) {
      const imageElement = new Image(30, 30);
      if (icon.data) {
        imageElement.src = `/assets/map-icons/${icon.data}`;
      } else {
        imageElement.src = `data:image/png;base64,${this.mapIconsService.getDefaultIcon()}`;
      }
      const promise = new Promise(resolve => {
        imageElement.onload = () => {
          this.map.addImage(`image-${icon.id ? icon.id : 'undefined'}`, imageElement);
          resolve(true);
        };
      });
      promises.push(promise);
      loadedRepresentationIconIds.push(icon.id);
    }
    Promise.all(promises).then(() => {
      this.addPoints(loadedRepresentationIconIds);
    });
  }

  private addPoints(loadedRepresentationIconIds: number[]): void {
    const features: any[] = [];
    for (const gisMachine of this.gisMachines) {
      const icon: string = loadedRepresentationIconIds.includes(gisMachine.iconId) ?
        `image-${gisMachine.iconId}` : 'image-undefined';
      features.push({
        type: 'Feature',
        properties: {
          machineUuid: gisMachine.machineUuid,
          iconId: gisMachine.iconId,
          icon: icon
        },
        geometry: {
          type: 'Point',
          coordinates: [
            gisMachine.longitude,
            gisMachine.latitude
          ]
        }
      });
    }
    this.mapData = {
      type: 'FeatureCollection',
      features: features
    };

    this.buildMainMap();
  }

  private buildMainMap(): void {
    this.map
      .addSource('data', {
        type: 'geojson',
        data: this.mapData,
        cluster: true,
        clusterMaxZoom: 22,
        clusterRadius: 15
      })
      .addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'data',
        filter: ['has', 'point_count'],
        paint: {
          'circle-opacity': 0.7,
          'circle-blur': 0.6,
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#81D4FA', 100,
            '#42A5F5', 750,
            '#1976D2'
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            30, 100,
            40, 750,
            80
          ]
        }
      })
      .addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'data',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12
        }
      })
      .addLayer({
        id: 'unclustered-points',
        type: 'symbol',
        source: 'data',
        filter: ['!', ['has', 'point_count']],
        layout: {
          'icon-allow-overlap': true,
          'icon-image': ['get', 'icon']
        }
      })
      .on('click', 'unclustered-points', (e) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        const machineUuid = e.features[0].properties.machineUuid;
        this.showMachineDetails(machineUuid, coordinates);
      })
      .on('click', 'clusters', (e) => {
        const feature = this.map.queryRenderedFeatures(e.point, {layers: ['clusters']})[0];
        const coordinates = feature.geometry.coordinates.slice();
        const clusterId = feature.properties.cluster_id;
        const pointCount = feature.properties.point_count;
        this.showClusterMachineList(clusterId, pointCount, coordinates);
      })
      .on('click', () => {
        this.filtersVisible = false;
      })
      .on('mouseenter', 'unclustered-points', () => {
        this.changeCursor('pointer');
      })
      .on('mouseenter', 'clusters', () => {
        this.changeCursor('pointer');
      })
      .on('mouseleave', 'unclustered-points', () => {
        this.changeCursor('');
      })
      .on('mouseleave', 'clusters', () => {
        this.changeCursor('');
      })
      .addControl(new MapLayerControl(document.getElementById('map-layer-control-filters')));
    if (this.authGuard.hasPermission(this.PERMISSION_MANAGE_ADVANCED_MACHINES)) {
      this.map.addControl(new MapLayerControl(document.getElementById('map-layer-control-clipboard')));
    }
    setTimeout(() => this.mapIsLoading = false, 1000);
  }

  private changeCursor(cursor: string): void {
    this.map.getCanvas().style.cursor = cursor;
  }

  private fitMapToData(): void {
    if (this.gisMachines.length > 0) {
      let minLat = 512, minLong = 512, maxLat = -512, maxLong = -512;
      this.gisMachines.forEach(
        gisMachine => {
          minLat = gisMachine.latitude < minLat ? gisMachine.latitude : minLat;
          maxLat = gisMachine.latitude > maxLat ? gisMachine.latitude : maxLat;
          minLong = gisMachine.longitude < minLong ? gisMachine.longitude : minLong;
          maxLong = gisMachine.longitude > maxLong ? gisMachine.longitude : maxLong;
        });
      setTimeout(
        () => {
          this.map?.fitBounds([
            [minLong, minLat],
            [maxLong, maxLat]
          ], {maxDuration: 1});
          this.map?.zoomOut();
        },
        1000);
    }
  }

  public showMachineDetails(machineUuid: string, coordinates: any): void {
    if (coordinates) {
      this.map.easeTo({center: coordinates});
    }
    this.detailsMode = 'machine';
    this.machineDetailsUuid = machineUuid;
    this.setDisplayDetails(true);
  }

  setDisplayDetails(active: boolean): void {
    this.displayDetails = active;
  }

  private showClusterMachineList(clusterId: number, pointCount: number, coordinates: any): void {
    const source = this.map.getSource('data');
    if (source.type === 'geojson') {
      source.getClusterExpansionZoom(clusterId, (err, newZoom) => {
        if (err) {
          return;
        }
        const currentZoom = this.map.getZoom();
        if (currentZoom >= 19) {
          const clusterSource = this.map.getSource('data');
          if (clusterSource.type === 'geojson') {
            clusterSource.getClusterLeaves(clusterId, pointCount, 0, (error, features) => {
              this.machineDetailsUuids = features.map(feature => feature.properties.machineUuid);
              this.detailsMode = 'cluster';
              this.setDisplayDetails(true);
            });
          }
        }
        this.map.easeTo({center: coordinates, zoom: newZoom > 19 ? 19 : newZoom});
      });
    }
  }

  private loadFilters(): void {
    this.buildTreeNode();
    this.machineModelsService.getModels().subscribe(response => {
      this.models = response.models;
    });
    this.loadGeocoding();

    this.groups = [];
    this.alarms = [];

    if (this.authGuard.hasPermission(this.PERMISSION_MANAGE_ADVANCED_MACHINES)) {
      this.machineGroupsService.getGroupsSummary().subscribe(response => {
        this.groups = response.machineGroups;
      });
    }
    if (this.authGuard.hasPermission('PERMISSION_VIEW_ALARMS')) {
      this.alarmsService.getAlarmsDefinitionFilters().subscribe(response => {
        this.alarms = response.alarmsDefinition;
      })
    }
  }

  private buildTreeNode(): void {
    this.hierarchyService.getHierarchyDropdown().subscribe(response => {
      let responseHierarchy: any[] = this.hierarchyService.getHierarchyTreeNodeFilter(response.hierarchyDto);
      this.hierarchy = responseHierarchy[1];
      this.organizationCodes = responseHierarchy[2].sort((a, b) => a.label > b.label ? 1 : -1);
    });
  }

  deleteChildren(organizationUuid, allNodes): any[] {
    if (allNodes[organizationUuid].children) {
      if (allNodes[organizationUuid].children.length === 0) {
        allNodes[organizationUuid] = {label: allNodes[organizationUuid].label, data: organizationUuid};
      } else {
        allNodes[organizationUuid].children.forEach(child => {
          allNodes = this.deleteChildren(child.data, allNodes);
        });
        allNodes[organizationUuid] = {label: allNodes[organizationUuid].label, data: organizationUuid};
      }
    }
    return allNodes;
  }

  private loadGeocoding(): void {
    this.isStateDisabled = true;
    this.isLocalityDisabled = true;
    this.countryStates = new Map<string, Set<string>>();
    this.stateLocalities = new Map<string, Set<string>>();
    this.geocodingService.getMachineGeocodings(this.language).subscribe(response => {
      this.geocodings = response.geocodings;
      let geocodingData = this.geocodingService.getGeocodingData(this.geocodings, this.language);
      this.countryStates = geocodingData.countryStates;
      this.stateLocalities = geocodingData.stateLocalities;
      this.countries = geocodingData.countries;
      this.states = geocodingData.states;
      this.localities = geocodingData.localities;
    });
  }

  trimFieldsForm(filters: MachineFilters): void {
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  toggleFilters(): void {
    this.filtersVisible = !this.filtersVisible;
  }

  canDeactivate(): Promise<boolean> {
    this.setDisplayDetails(false);
    return new Promise(resolve => {
      setTimeout(() => resolve(true), 0);
    });
  }

  onCountryChange(event): void {
    this.isStateDisabled = !(event.value && event.value.length > 0);
    this.isLocalityDisabled = true;
    this.currentFilters.states = [];
    this.currentFilters.localities = [];
  }

  onStateChange(event): void {
    this.isLocalityDisabled = !(event.value && event.value.length > 0);
    this.currentFilters.localities = [];
  }

  get filteredStates(): { name: string }[] {
    if (this.currentFilters.countries?.length > 0) {
      return this.states.filter(state => {
        for (const country of this.currentFilters.countries) {
          if (this.countryStates.get(country).has(state.name)) {
            return true;
          }
        }
        return false;
      });
    }
    return this.states;
  }

  get filteredLocalities(): { name: string }[] {
    if (this.currentFilters.states?.length > 0) {
      return this.localities.filter(locality => {
        for (const state of this.currentFilters.states) {
          if (this.stateLocalities.get(state).has(locality.name)) {
            return true;
          }
        }
        return false;
      });
    }
    return this.localities;
  }

  gisMachinesJson(): string {
    if (this.authGuard.hasPermission(this.PERMISSION_MANAGE_ADVANCED_MACHINES)) {
      return JSON.stringify(this.gisMachines);
    } else {
      return '';
    }
  }

  applyFilters(): void {
    this.mapIsLoading = true;
    this.deleted = this.currentFilters.deleted;
    this.currentFilters.organizationUuids = [];
    if (this.organizations != null && this.organizations.length > 0) {
      this.organizations.forEach(organization => {
        this.currentFilters.organizationUuids.push(organization.data);
      });
    }
    this.loadMapAndMachinesData();
  }

  resetFilters(): void {
    this.currentFilters = new MachineFilters();
    this.deleted = false;
    this.currentFilters.deleted = false;
    this.organizations = [];
  }

  ngOnDestroy(): void {
    this.map.remove();
  }

  adjustTextColor(colorHex): string {
    return this.colorService.adjustTextColor(colorHex);
  }

}
